import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import AdminLayout from "../../../layouts/AdminLayout";
import Loader from "../../../components/loader";
import TableHeader from "../../../components/table/tableHeader";
import { adminPaths } from "../../../routes/admin";
import { pipViewDate, pipViewDate2 } from "../../../utils/pip";
import { fetchAdminJobApplications } from "../../../redux/features/admin/actions/projectAction";

const tabOptions = [
  { value: "all", label: "All Applicants" },
  { value: "quick_apply", label: "Quick Apply" },
];

const toValidUrl = (urlValue) => {
  const raw = String(urlValue || "").trim();
  if (!raw) return "";
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
};

const getApplicantSkills = (applicant) =>
  (Array.isArray(applicant?.skills)
    ? applicant?.skills
    : String(applicant?.skills || applicant?.key_skills || "")
      .split(",")
      .map((item) => String(item || "").trim())
      .filter(Boolean));

const AdminJobApplicants = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isLoading,
    jobApplications,
    selectedJob,
    selectedJobId,
    selectedJobTab,
    jobApplicationCounts,
  } = useSelector((state) => state?.admin?.project);

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const queryJobId = params.get("job_id");
  const resolvedJobId = String(queryJobId || selectedJobId || "");
  const [activeTab, setActiveTab] = useState("all");
  const [activeApplicant, setActiveApplicant] = useState(null);

  useEffect(() => {
    if (selectedJobTab) {
      setActiveTab(selectedJobTab);
    }
  }, [selectedJobTab]);

  useEffect(() => {
    if (!resolvedJobId) return;
    dispatch(
      fetchAdminJobApplications({
        tab: activeTab,
        job_id: resolvedJobId,
      }),
    );
  }, [dispatch, resolvedJobId, activeTab]);

  return (
    <AdminLayout>
      {isLoading && <Loader />}
      <div className="ct_px_40 mt-4 pb-4">
        <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap mb-4">
          <div>
            <h4 className="mb-1 ct_fs_20 ct_darkblue_text">
              Job Applicants
            </h4>
            <p className="mb-0 ct_text_clr_757886">
              Review full applicant submissions for this job post.
            </p>
          </div>
          <button
            type="button"
            className="ct_outline_btn px-3 py-2"
            onClick={() => navigate(adminPaths?.JobProject)}
          >
            <i className="fa-solid fa-arrow-left me-2"></i>Back to Projects & Jobs
          </button>
        </div>

        <div className="ct_outline_bg ct_box_shadow_card_1 ct_border_radius_10 p-4 mb-4">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <h5 className="mb-0 ct_fs_18 ct_darkblue_text">
              {selectedJob?.job_title || "Job Details"}
            </h5>
            <span className="badge bg-light text-dark text-capitalize">
              {selectedJob?.status || "N/A"}
            </span>
          </div>
          <div className="row g-3">
            <div className="col-lg-4 col-md-6">
              <p className="mb-1 ct_fs_12 ct_grey_text">Category</p>
              <p className="mb-0 ct_fs_14 ct_darkblue_text">{selectedJob?.category || "#N/A"}</p>
            </div>
            <div className="col-lg-4 col-md-6">
              <p className="mb-1 ct_fs_12 ct_grey_text">Availability</p>
              <p className="mb-0 ct_fs_14 ct_darkblue_text">{selectedJob?.availability || "#N/A"}</p>
            </div>
            <div className="col-lg-4 col-md-6">
              <p className="mb-1 ct_fs_12 ct_grey_text">Work Approach</p>
              <p className="mb-0 ct_fs_14 ct_darkblue_text">{selectedJob?.candidate_location || "#N/A"}</p>
            </div>
            <div className="col-lg-4 col-md-6">
              <p className="mb-1 ct_fs_12 ct_grey_text">Location</p>
              <p className="mb-0 ct_fs_14 ct_darkblue_text">{selectedJob?.location || "#N/A"}</p>
            </div>
            <div className="col-lg-4 col-md-6">
              <p className="mb-1 ct_fs_12 ct_grey_text">Experience</p>
              <p className="mb-0 ct_fs_14 ct_darkblue_text">
                {selectedJob?.years_of_experience ? `${selectedJob?.years_of_experience} Years` : "#N/A"}
              </p>
            </div>
            <div className="col-lg-4 col-md-6">
              <p className="mb-1 ct_fs_12 ct_grey_text">License</p>
              <p className="mb-0 ct_fs_14 ct_darkblue_text">{selectedJob?.license || "#N/A"}</p>
            </div>
            <div className="col-lg-4 col-md-6">
              <p className="mb-1 ct_fs_12 ct_grey_text">Salary</p>
              <p className="mb-0 ct_fs_14 ct_darkblue_text">
                {selectedJob?.salary_currency || ""} {selectedJob?.min_salary || "0"} - {selectedJob?.max_salary || "0"}
              </p>
            </div>
            <div className="col-lg-4 col-md-6">
              <p className="mb-1 ct_fs_12 ct_grey_text">Company</p>
              <p className="mb-0 ct_fs_14 ct_darkblue_text">{selectedJob?.company_name || "#N/A"}</p>
            </div>
            <div className="col-lg-4 col-md-6">
              <p className="mb-1 ct_fs_12 ct_grey_text">Posted On</p>
              <p className="mb-0 ct_fs_14 ct_darkblue_text">
                {pipViewDate(selectedJob?.posted_date) || "#N/A"}
              </p>
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center flex-wrap gap-3 mb-3 border-bottom pb-2">
          {tabOptions.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className="ct_fs_14 border-0 bg-transparent pb-1"
              onClick={() => setActiveTab(tab.value)}
              style={{
                color: activeTab === tab.value ? "#219EBC" : "var(--dark_blue)",
                borderBottom: activeTab === tab.value ? "2px solid var(--sky_blue)" : "2px solid transparent",
                fontWeight: activeTab === tab.value ? 600 : 400,
              }}
            >
              {tab.label} ({jobApplicationCounts?.[tab.value] || 0})
            </button>
          ))}
        </div>

        <div className="table-responsive ct_custom_table pt-2">
          <table className="table">
            <TableHeader data={["#", "Applicant", "Email", "Phone", "Status", "Applied At", "Action"]} />
            <tbody>
              {jobApplications?.length > 0 ? (
                jobApplications.map((applicant, index) => (
                  <tr key={applicant?.applied_id || applicant?.id || index}>
                    <td>{index + 1}</td>
                    <td>{applicant?.full_name || "#N/A"}</td>
                    <td>{applicant?.email || "#N/A"}</td>
                    <td>{applicant?.phone_number || "#N/A"}</td>
                    <td className="text-capitalize">{applicant?.status || "#N/A"}</td>
                    <td>{pipViewDate2(applicant?.created_at || applicant?.applied_at) || "#N/A"}</td>
                    <td>
                      <button
                        className="ct_btn_apply_v2"
                        type="button"
                        onClick={() => setActiveApplicant(applicant)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center ct_grey_text">
                    No applicants found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {activeApplicant && (
        <div className="modal d-block ct_congratulation_modal_fade" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered ct_modal-dialog" style={{ maxWidth: "860px" }}>
            <div className="modal-content ct_border_radius_20">
              <div className="modal-header border-0 align-items-center justify-content-between">
                <div>
                  <h4 className="ct_fs_20 mb-1 ct_darkblue_text">Applicant Overview</h4>
                  <p className="mb-0 ct_fs_14 ct_grey_text">Full submitted details</p>
                </div>
                <button
                  type="button"
                  className="btn-close ct_circle_close"
                  onClick={() => setActiveApplicant(null)}
                ></button>
              </div>
              <div className="modal-body pt-0">
                <form className="ct_modal_scroll ct_custom_scroll px-4 pb-3">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">Full Name</label>
                        <input className="form-control ct_input" value={activeApplicant?.full_name || ""} readOnly />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">Email Address</label>
                        <input className="form-control ct_input" value={activeApplicant?.email || ""} readOnly />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">Phone Number</label>
                        <input className="form-control ct_input" value={activeApplicant?.phone_number || ""} readOnly />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">Location</label>
                        <input className="form-control ct_input" value={activeApplicant?.location || activeApplicant?.current_position || ""} readOnly />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">Experience</label>
                        <input className="form-control ct_input" value={activeApplicant?.years_of_experience || ""} readOnly />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">Availability</label>
                        <input className="form-control ct_input" value={activeApplicant?.availability || activeApplicant?.work_type || ""} readOnly />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">Skills</label>
                        <div className="form-control ct_input h-auto py-2 d-flex align-items-center gap-2 flex-wrap">
                          {getApplicantSkills(activeApplicant)?.length > 0 ? (
                            getApplicantSkills(activeApplicant).map((skill, idx) => (
                              <span key={`${skill}-${idx}`} className="ct_skill_badge">
                                {skill}
                              </span>
                            ))
                          ) : (
                            <span className="ct_grey_text">#N/A</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">LinkedIn Profile Link</label>
                        <input className="form-control ct_input" value={toValidUrl(activeApplicant?.linkedin_url)} readOnly />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">Portfolio Link</label>
                        <input className="form-control ct_input" value={toValidUrl(activeApplicant?.portfolio_url)} readOnly />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">Cover Letter</label>
                        <textarea
                          className="form-control ct_input h-auto"
                          rows={4}
                          value={activeApplicant?.cover_letter || ""}
                          readOnly
                        ></textarea>
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">Resume</label>
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                          <input className="form-control ct_input" value={activeApplicant?.resume_url || ""} readOnly />
                          {activeApplicant?.resume_url && (
                            <a
                              href={activeApplicant?.resume_url}
                              target="_blank"
                              rel="noreferrer"
                              className="ct_outline_btn px-3 py-2 ct_fs_12"
                              style={{ height: "38px" }}
                            >
                              View Resume
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminJobApplicants;
